import { Icon, Menu, type MenuProps, Tooltip } from '@eleo-rh/core-ui'
import { toast } from '@eleo-rh/core-ui/contexts'
import axios from 'axios'
import InformationTooltip from 'components/InformationTooltip'
import Button from 'components/UI/CoreUIButton'
import {
  API_URL_DOC_TEMPLATES,
  API_URL_DOC_TEMPLATES_ACCREDITATION_PASSPORT_DOWNLOAD as downloadURL,
  API_URL_USER_ACCREDITATION_PASSPORT_QR_CODE as qrCodeURL,
} from 'constants/api'
import { CONTEXT_ACCREDITATION_PASSPORT_CODE } from 'constants/docTemplate'
import { useI18n } from 'context/I18nProvider'
import { useBlob } from 'hooks/binary'
import useSWR from 'hooks/swr'
import { useState } from 'react'
import { urlAddParams } from 'services/url'

interface AccreditationPassportDownloadProps {
  userId: number
  disabled?: boolean
  onCustom: () => void
}

const STANDARD_KEY = 'standard'
const QR_CODE_KEY = 'qrcode'
const CUSTOM_KEY = 'custom'

export default function AccreditationPassportDownload({
  userId,
  disabled = false,
  onCustom,
}: AccreditationPassportDownloadProps) {
  const { i18n } = useI18n()
  const { downloadBlob } = useBlob()

  const [posting, setPosting] = useState(false)

  const { data: templates = [], isValidating } = useSWR(
    urlAddParams(API_URL_DOC_TEMPLATES, { context: CONTEXT_ACCREDITATION_PASSPORT_CODE }),
  ) as {
    data: { id: number; formats: string[]; default: boolean }[] | undefined
    isValidating: boolean
  }
  const defaultTemplate = templates.find(template => template.default)
  const { formats, id: templateId } = defaultTemplate || { formats: [], id: null }

  const handleStandardDownload = async (format: string) => {
    const downloadUrl = urlAddParams(downloadURL, { format, user: userId, template: templateId })
    setPosting(true)
    try {
      const downloaded = await downloadBlob(downloadUrl, '_blank', undefined, {
        validateStatus: (status: number) => status >= 200 && status < 300,
      })
      if (downloaded) toast.success({ title: i18n('notification.generic.success'), duration: 3 })
    } catch (error) {
      toast.error({
        title: i18n('label.error'),
        description:
          (axios.isAxiosError<{ 'hydra:description'?: string }>(error) &&
            error.response?.data?.['hydra:description']) ||
          i18n('accreditation.passport.generation_failed'),
        duration: 3,
      })
    } finally {
      setPosting(false)
    }
  }

  const handleQrCodeDownload = async () => {
    const downloadUrl = qrCodeURL.replace('ID', String(userId))
    setPosting(true)
    try {
      const downloaded = await downloadBlob(downloadUrl, '_blank', undefined, {
        validateStatus: (status: number) => status >= 200 && status < 300,
      })
      if (downloaded) toast.success({ title: i18n('notification.generic.success'), duration: 3 })
    } catch (error) {
      toast.error({
        title: i18n('label.error'),
        description:
          (axios.isAxiosError<{ 'hydra:description'?: string }>(error) &&
            error.response?.data?.['hydra:description']) ||
          i18n('accreditation.passport.qr_code_generation_failed'),
        duration: 3,
      })
    } finally {
      setPosting(false)
    }
  }

  const handleMenuClick: MenuProps['onClickItems'] = ({ key }) => {
    if (key === QR_CODE_KEY) {
      handleQrCodeDownload()
    } else if (key === CUSTOM_KEY) {
      onCustom()
    } else if (key === STANDARD_KEY) {
      handleStandardDownload(formats[0])
    } else if (key === `${STANDARD_KEY}:docx` || key === `${STANDARD_KEY}:pdf`) {
      handleStandardDownload(key.split(':')[1])
    }
  }

  const labelWithInfo = (label: string, tooltip: string) => (
    <span className="flex items-center">
      {label}
      <button
        type="button"
        className="inline-flex items-center bg-transparent border-0 p-0 cursor-default"
        onClick={e => e.stopPropagation()}
      >
        <InformationTooltip messages={[tooltip]} />
      </button>
    </span>
  )

  if (!templates.length) return null

  const standardLabel = labelWithInfo(
    i18n('accreditation.passport.dropdown.standard_label'),
    i18n('accreditation.passport.dropdown.standard_tooltip'),
  )

  const standardItem: NonNullable<MenuProps['items']>[number] =
    formats.length > 1
      ? {
          key: STANDARD_KEY,
          label: standardLabel,
          children: [
            {
              key: `${STANDARD_KEY}:docx`,
              label: 'Word',
              icon: <Icon name="file-word" />,
            },
            {
              key: `${STANDARD_KEY}:pdf`,
              label: 'Pdf',
              icon: <Icon name="file-pdf" />,
            },
          ],
        }
      : { key: STANDARD_KEY, label: standardLabel }

  const items: MenuProps['items'] = [
    ...(defaultTemplate && formats.length
      ? [
          standardItem,
          {
            key: QR_CODE_KEY,
            label: labelWithInfo(
              i18n('accreditation.passport.dropdown.qr_code_label'),
              i18n('accreditation.passport.dropdown.qr_code_tooltip'),
            ),
          },
        ]
      : []),
    {
      key: CUSTOM_KEY,
      label: i18n('accreditation.passport.dropdown.custom_label'),
    },
  ]

  if (disabled) {
    return (
      <Tooltip title={i18n('accreditation.passport.cannot_generate')}>
        <span>
          <Button leftIcon="arrow-down-to-bracket" disabled>
            {i18n('profile.action.download_accreditation_passport')}
          </Button>
        </span>
      </Tooltip>
    )
  }

  return (
    <Menu items={items} onClickItems={handleMenuClick}>
      <Button leftIcon="arrow-down-to-bracket" rightIcon="chevron-down" loading={posting || isValidating}>
        {i18n('profile.action.download_accreditation_passport')}
      </Button>
    </Menu>
  )
}
